import { motion } from 'framer-motion';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { SideBarContent, MainContent } from './components';

import { Divider, Loader } from '@/components';
import { Layout } from '@/components/GenericsTournaments';
import { DefaultTabsProps, Tabs } from '@/components/Tabs';
import { TabType } from '@/components/Types';
import useAuth from '@/hooks/useAuth';
import useFriendsCup from '@/hooks/useFriendsCup';
import { useHandleChangesTabs } from '@/hooks/useHandleChangesTabs';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { useIsLastMatchDay } from '@/hooks/useIsLastMatchDate';
import { useMetaData } from '@/hooks/useMetaData';
import useToast from '@/hooks/useToast';
import useTourney from '@/hooks/useTourney';
import ROUTES from '@/router/routes';
import {
  getFullInitFCup,
  getInitFCup,
  requestTop10FCup,
} from '@/services/friendsCup';
import { InitFCup, ListFCup, RecommendedListFCup } from '@/types/friendCup';
import { FADE_IN_OUT, TEXTS } from '@/utils';
import { SECTION_NAME_FOR_ADS } from '@/utils/ads';

interface State {
  init: InitFCup;
  recommendedList: RecommendedListFCup | [];
  pendingList: ListFCup | [];
  playingList: ListFCup | [];
  invitationList: ListFCup | [];
  mounted: boolean;
}

const initialState: State = {
  init: null,
  recommendedList: [],
  pendingList: [],
  invitationList: [],
  playingList: [],
  mounted: false,
};

export const HomeFCup = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { handleTabChanges, tab } = useHandleChangesTabs();
  const [state, setState] = useState<State>(initialState);
  const { tourneyData, updateTourney, handleSetTourney } = useTourney();
  const { isMobile } = useIsDesktop();
  const { userInitialData: userLogged } = useAuth();
  const isLastMatchDay = useIsLastMatchDay();
  const { groupData, handleSetGroup } = useFriendsCup();
  const { data, isRefetching, isLoading } = useQuery(
    'getFullInitFCup',
    async () => await getFullInitFCup(),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: false,
    }
  );

  useMetaData({
    title: 'Copa de Amigos',
  });

  // set local state
  useEffect(() => {
    if (
      data?.init?.estado &&
      data?.recommended.estado &&
      !state.mounted &&
      !isLoading &&
      !isRefetching
    ) {
      const { init, recommended } = data;

      // set estado global
      updateTourney({
        inscriptionOpen: init.inscripcionAbierta,
        tourneysPlaying: init.grupos || [],
        tourneysPending: init.gruposPendAceptacionDuenio || [],
        tourneysInvitations: init.gruposPend || [],
      });

      handleSetGroup({
        ...groupData,
        txtFechaReemplazar: data.init.txtFechaComienzoProximoGrupoCreado,
      });

      // set estado local
      setState((prevState) => ({
        ...prevState,
        init,
        recommendedList: recommended.gruposSugeridos || [],
        invitationList: init.gruposPend || [],
        playingList: init.grupos || [],
        pendingList: init.gruposPendAceptacionDuenio || [],
        mounted: true,
      }));
    }
  }, [
    data,
    isLoading,
    isRefetching,
    state.mounted,
    updateTourney,
    tourneyData,
  ]);

  const disabledTop10 = useMemo(() => {
    return (
      !(
        state.init &&
        state.init.ultimaVersionPublicada &&
        state.init.ultimaVersionPublicada > 0 &&
        !state.init.vedaUltFechaSinParticipar
      ) || false
    );
  }, [state.init]);

  const { data: dataTop10, isLoading: isLoadingTop10 } = useQuery(
    'requestTop10FCup',
    async () => {
      if (data?.init?.estado) {
        return await requestTop10FCup(data.init.ultimaVersionPublicada || 0);
      }
    },
    {
      refetchOnWindowFocus: false,
      keepPreviousData: false,
      enabled: !disabledTop10,
    }
  );

  // clean state
  useEffect(() => {
    return () => {
      setState(initialState);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tabs: DefaultTabsProps['tabs'] = useMemo(() => {
    return [
      { name: 'Mis Equipos' },
      {
        name: 'Top 10',
        disabled: !(
          state.init &&
          state.init.ultimaVersionPublicada &&
          state.init.ultimaVersionPublicada > 0
        ),
      },
    ];
  }, [state.init]);

  // refresco pendientes de aceptacion al invitar postulacion
  const onHandleRefreshAfterInvitation = useCallback(
    async (pendings = false) => {
      const refreshData = await getInitFCup();
      if (refreshData?.estado) {
        if (pendings) {
          setState((prevState) => ({
            ...prevState,
            pendingList: refreshData.gruposPendAceptacionDuenio || [],
            invitationList: refreshData.gruposPend || [],
          }));
        } else {
          setState((prevState) => ({
            ...prevState,
            invitationList: refreshData.gruposPend || [],
            pendingList: refreshData.gruposPendAceptacionDuenio || [],
            playingList: refreshData.grupos || [],
          }));
        }
        handleSetTourney({
          ...tourneyData,
          tourneysPending: refreshData.gruposPendAceptacionDuenio || [],
          tourneysInvitations: refreshData.gruposPend || [],
          tourneysPlaying: refreshData.grupos || [],
        });
      } else {
        toast.show(TEXTS.defaultError);
      }
    },
    [handleSetTourney, toast, tourneyData]
  );

  // effecto escucha al enviar postulacion
  useEffect(() => {
    if (tourneyData.idsPendings.length > 0 && state.mounted) {
      onHandleRefreshAfterInvitation(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourneyData.idsPendings]);

  // effecto escucha al aceptar invitaciones
  useEffect(() => {
    if (tourneyData.idsPlayings.length > 0 && state.mounted) {
      onHandleRefreshAfterInvitation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourneyData.idsPlayings]);

  const MainTabs = useCallback(() => {
    return <Tabs activeTab={tab} tabs={tabs} onClick={handleTabChanges} />;
  }, [handleTabChanges, tab, tabs]);

  const keyMobile = useMemo(() => {
    const key = isMobile ? Math.random() : false;
    return key ? tab + key : TabType.SecondTab;
  }, [isMobile, tab]);

  return (
    <>
      <Loader isVisible={!state.mounted || isLoadingTop10} />
      <Layout
        actionFooter={{
          buttonName: 'Crear equipo',
          buttonDisabled: isLastMatchDay || !tourneyData.inscriptionOpen,
          actionHandler() {
            if (userLogged?.dtExtranjero) {
              return toast.show(TEXTS.exclusiveForArgentinianDTs);
            }
            navigate(ROUTES.friendsCup.createTeam);
          },
        }}
        adsSections={SECTION_NAME_FOR_ADS.copadeamigos}
        className="px-4"
        main={() => (
          <div className="lg:pr-[111px] lg:min-h-[600px] flex-1">
            <MainContent
              invitationList={state.invitationList}
              isMounted={state.mounted}
              pendingList={state.pendingList}
              playingList={state.playingList}
              refetchFather={onHandleRefreshAfterInvitation}
              tabSelected={tab}
              tabs={<MainTabs />}
              top10List={dataTop10}
            />
          </div>
        )}
        mobile={{
          leftContent: {
            showMenuAction: true,
            items: true,
          },
          centerContent: { title: 'Copa de Amigos' },
        }}
        sidebar={() => (
          <motion.div
            key={keyMobile}
            animate={'show'}
            className="min-h-[700px]"
            initial="hide"
            variants={FADE_IN_OUT}
          >
            <div className="lg:w-[334px]">
              {Boolean(
                state.pendingList?.length ||
                  state.playingList?.length ||
                  state.invitationList?.length
              ) && (
                <Divider
                  className="mt-6 mb-7 h-[2px] lg:hidden"
                  type="horizontal"
                />
              )}
              <SideBarContent
                isData={
                  state.invitationList.length > 0 ||
                  state.playingList.length > 0 ||
                  state.pendingList.length > 0
                }
                list={state.recommendedList || []}
              />
            </div>
          </motion.div>
        )}
        tabs={() => isMobile && <MainTabs />}
      />
    </>
  );
};
